import { Card, CardHeader, Stack } from '@mui/material';
import { Text } from 'components/text/Text';
import { Button } from '@nps.today/component-library';
import CustomSwitch from 'components/util/CustomSwitch';
import { Role } from 'access';
import useAuth from 'hooks/useAuth';
import { useEffect, useState } from 'react';
import useCustomDispatch from 'redux/dispatch';
import { getCurrentTenant, updateCurrentTenant } from 'redux/slices/organization';
import { RootState, useSelector } from 'redux/store';
import { useTranslator } from 'translation/useTranslator';
import { ChildTenant } from '../../../@types/organization';

const componentDataTestId = 'authentication-configuration';

export const LoginConfiguration = () => {
  const translator = useTranslator();
  const auth = useAuth();
  const customDispatch = useCustomDispatch();
  const { loading } = useSelector((state: RootState) => state.organization);
  const isLoading = !!loading.getCurrentTenant || !!loading.tenant;

  const [tenant, setTenant] = useState<ChildTenant | null>(null);
  const [disableFormLogin, setDisableFormLogin] = useState(false);
  const [enableMfaRequirement, setEnableMfaRequirement] = useState(false);

  useEffect(() => {
    customDispatch({
      action: getCurrentTenant,
      disableSuccessMessage: true,
      onSuccess: (response) => setTenant(response.result?.data)
    });
  }, [customDispatch]);

  useEffect(() => {
    setDisableFormLogin(tenant?.isPasswordLoginDisabled ?? false);
    setEnableMfaRequirement(tenant?.isMfaForced ?? false);
  }, [tenant?.isMfaForced, tenant?.isPasswordLoginDisabled]);

  const mfaChanged = enableMfaRequirement !== (tenant?.isMfaForced ?? false);
  const formLoginChanged = disableFormLogin !== (tenant?.isPasswordLoginDisabled ?? false);

  return auth.user?.role === Role.CompanyUser ? null : (
    <Card sx={{ padding: 2, marginBottom: 2, width: '100%' }}>
      <CardHeader
        sx={{ p: 0 }}
        title={translator.loginConfiguration()}
        action={
          <Button
            disabled={!tenant || (!mfaChanged && !formLoginChanged)}
            loading={!!loading.tenant}
            dataTestId={`${componentDataTestId}-save`}
            variant="contained"
            onClick={async () => {
              if (!tenant || (!formLoginChanged && !mfaChanged)) {
                return;
              }

              await customDispatch({
                action: updateCurrentTenant,
                successMessage: translator.loginConfigurationUpdated(),
                actionParameters: {
                  ...tenant,
                  isMfaForced: enableMfaRequirement,
                  isPasswordLoginDisabled: disableFormLogin
                },
                onSuccess: (response) => setTenant(response.result?.data)
              });
            }}
          >
            {translator.save()}
          </Button>
        }
      />
      <Stack>
        <CustomSwitch
          label={translator.disableFormLogin()}
          switchProps={{
            disabled: isLoading,
            checked: disableFormLogin,
            onChange: (event, value) => {
              setDisableFormLogin(value);
            }
          }}
        />
        <Text variant="caption">{translator.disableFormLoginDescription()}</Text>
        <CustomSwitch
          label={translator.enableMfaRequirement()}
          switchProps={{
            disabled: isLoading,
            checked: enableMfaRequirement,
            onChange: (event, value) => {
              setEnableMfaRequirement(value);
            }
          }}
        />
        <Text variant="caption">{translator.enableMfaRequirementDescription()}</Text>
      </Stack>
    </Card>
  );
};
